import { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { post, getApiErrorMessage } from "../helper/APIClient";
import { Routes } from "../helper/Routes";
import { clearPendingPromotionCode, getPendingPromotionCode } from "../helper/promotion";
import { usePackage } from "../context/usePackage";
import { type PromotionValidation } from "../helper/promotion";
import { writeDirectDebitSetupContext, type DirectDebitSetupContext } from "../helper/directDebitSetupContext";

const CONTAINER_ID = "promotion-claim";

type RedeemResult = PromotionValidation & { checkout?: DirectDebitSetupContext & { authorisationUrl: string } };

// Claims a promotion the user entered during sign-up (or arrived with via ?promo=) once they are
// authenticated. The server re-validates everything, including that this account's email matches
// the recipient the code was issued to.
export default function PromotionClaimer() {
  const attempted = useRef(false);
  const [offer, setOffer] = useState<RedeemResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The decrypted vault key only ever lives in memory (see helper/vaultKeyStore.ts), by design, so it
  // is never persisted, and never survives a full page reload. Refresh the package context in place
  // instead of reloading the page - a reload would silently force the user back through /unlock right
  // after telling them their offer was claimed.
  const { refreshPackage } = usePackage();

  useEffect(() => {
    if (attempted.current) return;
    const code = getPendingPromotionCode();
    if (!code) return;
    attempted.current = true;

    void (async () => {
      try {
        const result = await post<RedeemResult>(Routes.PROMOTIONS_REDEEM, { code });
        if (result.requiresPayment) { setOffer(result); return; }
        clearPendingPromotionCode();
        toast.success(result.message || "Your offer has been claimed.", { containerId: CONTAINER_ID });
        await refreshPackage();
      } catch (error) {
        clearPendingPromotionCode();
        toast.error(
          getApiErrorMessage(error, "The promotion code is invalid or cannot be used with this email address."),
          { containerId: CONTAINER_ID, autoClose: 10000 },
        );
      }
    })();
  }, []);

  const startCheckout = async () => {
    setBusy(true); setError("");
    try {
      const result = await post<RedeemResult>(Routes.PROMOTIONS_REDEEM,
        { code: getPendingPromotionCode(), confirmMonthlyPayment: true });
      if (!result.checkout?.authorisationUrl) throw new Error("Checkout is unavailable. Please try again.");
      const destination = new URL(result.checkout.authorisationUrl);
      if (destination.protocol !== "https:" || !(destination.hostname === "gocardless.com" || destination.hostname.endsWith(".gocardless.com")))
        throw new Error("The payment provider returned an invalid checkout address.");
      writeDirectDebitSetupContext(result.checkout);
      clearPendingPromotionCode();
      window.location.assign(destination.href);
    } catch (e) { setError(getApiErrorMessage(e, "Unable to start checkout. Please try again.")); setBusy(false); }
  };

  return <>
    <ToastContainer containerId={CONTAINER_ID} position="top-center" />
    {offer && <section className="promotion-expiry-notice" aria-label="Review your promotional offer">
      <strong>{offer.promotionName}: {offer.productName}</strong>
      <p>£{Number(offer.price).toFixed(2)} per month for {offer.freeMonths} months, starting when your Direct Debit becomes active.
        Your offer ends automatically, monthly promotional payments stop, and access returns to Freemium.
        We will warn you during the final two weeks.</p>
      {error && <p role="alert">{error}</p>}
      <button type="button" disabled={busy} onClick={() => void startCheckout()}>{busy ? "Opening checkout…" : "Agree and continue to Direct Debit"}</button>
      <button type="button" disabled={busy} onClick={() => { clearPendingPromotionCode(); setOffer(null); }}>No thanks</button>
    </section>}
  </>;
}
